/**
 * Content Generation Alpine.js module.
 *
 * Provides SSE listener for render-job-completed events, SortableJS
 * drag-to-reorder on photo strips, and a helper to register the listener
 * on the window. The main form state (refreshPreview, startRender, etc.)
 * lives inline in the Templ x-data attribute because it is tightly
 * coupled to the template variables.
 */

import Sortable from "sortablejs";
import { liveSettleTarget } from "./htmx-helpers";

/** Initialize SortableJS on a photo strip element.
 *  After drag-end, syncs the Alpine `photos` array to match the new DOM order.
 */
export function initPhotoStripSortable(stripEl: HTMLElement, alpineComponent: any) {
  // Destroy previous instance if any
  const existing = (stripEl as any)._sortable;
  if (existing) existing.destroy();

  const instance = Sortable.create(stripEl, {
    animation: 150,
    ghostClass: "opacity-30",
    onEnd: () => {
      // Read new order from DOM data-item-id attributes
      const newOrder: string[] = [];
      stripEl.querySelectorAll("[data-item-id]").forEach((el) => {
        const id = (el as HTMLElement).dataset.itemId;
        if (id) newOrder.push(id);
      });
      if (newOrder.length > 0 && alpineComponent) {
        alpineComponent.photos = newOrder;
        // Trigger HTMX re-render to rebuild scene cards with new photo order.
        // Collects current variable values to preserve form state.
        const form = alpineComponent.$refs?.genForm;
        if (form && (window as any).htmx) {
          const qs = new URLSearchParams();
          qs.set("photos", newOrder.join(","));
          form.querySelectorAll("[data-var-key]").forEach((el: HTMLInputElement) => {
            if (el.value) qs.set("var_" + el.dataset.varKey, el.value);
          });
          (window as any).htmx.ajax(
            "GET",
            "/marketing/create/form/" + alpineComponent.templateId + "?" + qs.toString(),
            { target: "#content-generation-main", swap: "innerHTML" },
          );
        }
      }
    },
  });
  (stripEl as any)._sortable = instance;
}

// Expose globally so the inline Alpine x-data can call it
(window as any).initPhotoStripSortable = initPhotoStripSortable;

/** Listen for SSE render-job-completed events and dispatch a window event. */
export function setupContentGenerationListeners() {
  // The SSE broker already pushes events to the EventSource on the page.
  // We listen for the custom DOM event that the SSE helper dispatches.
  window.addEventListener("render-job-completed", ((e: CustomEvent) => {
    // The SSE bridge sends { detail: { data: "json string" } }
    // Parse the JSON data string so Alpine gets typed fields directly
    let parsed: any = {};
    try {
      const raw = e.detail?.data;
      parsed = typeof raw === "string" ? JSON.parse(raw) : raw || {};
    } catch {
      /* ignore parse errors */
    }
    window.dispatchEvent(new CustomEvent("content-generation:render-done", { detail: parsed }));
  }) as EventListener);

  // Ensure Alpine initializes x-data on HTMX-swapped content (e.g., form loaded by sidebar click).
  // Without this, Alpine directives like x-text referencing x-data variables fail with "not defined".
  //
  // Two distinct cases to handle safely:
  //   1. Target itself declares x-data → init the whole subtree (sets up the scope).
  //   2. Target has x-data descendants only → init each descendant individually.
  //      We must NOT init the whole target, because non-x-data children often reference
  //      a parent Alpine scope that lives OUTSIDE the swapped target (e.g., tab buttons
  //      inside a swap target whose activeTab lives on a grandparent div). Re-running
  //      initTree on those children breaks scope resolution for the parent-scope reference.
  document.addEventListener("htmx:afterSettle", ((e: CustomEvent) => {
    // liveSettleTarget skips detached targets. An hx-swap="outerHTML" swap (e.g.
    // the PageHeader back button replacing #main-content) leaves detail.target
    // pointing at the REPLACED, now-detached old subtree. Re-running initTree on
    // it re-processes directives like x-teleport against a document that no
    // longer contains their target (#add-group-modal) → Alpine crashes on
    // appendChild(null). The new content is initialized by Alpine's own
    // MutationObserver, so skipping is safe.
    const target = liveSettleTarget(e);
    const Alpine = (window as any).Alpine;
    if (!target || !Alpine) return;

    if (target.hasAttribute?.("x-data")) {
      Alpine.initTree(target);
      return;
    }
    target.querySelectorAll?.<HTMLElement>("[x-data]").forEach((el) => {
      Alpine.initTree(el);
    });
  }) as EventListener);
}
